import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, ShieldCheck, Landmark, Wallet, Zap, FileCheck, CheckCircle2 } from 'lucide-react';
import image from '../assets/image.png';

const SubsidySection = () => {
  const financialBenefits = [
    { icon: Landmark, title: 'Direct Bank Transfer', desc: 'Subsidy credited to your account.' },
    { icon: Wallet, title: 'Zero Tax Liability', desc: 'No tax on electricity you generate.' },
    { icon: Zap, title: 'Net Metering', desc: 'Earn by exporting extra power.' },
    { icon: FileCheck, title: 'Business Depreciation', desc: 'Claim accelerated depreciation.' },
  ];

  const eligibility = [
    'Valid Electricity Bill', 
    'Shadow-free rooftop',
    'Aadhar & Bank linked', 
    'No pending DISCOM dues',
  ];

  return (
    <section id="subsidies" className="py-12 sm:py-16 bg-[#050B14] relative overflow-hidden font-sans border-y border-[#1E5CB3]/20">
      
      {/* Ambient Glows */}
      <div className="absolute top-0 right-0 w-[250px] sm:w-[400px] h-[250px] sm:h-[400px] bg-[#F7941D]/10 rounded-full blur-[80px] sm:blur-[120px] pointer-events-none" />
      <div className="absolute bottom-0 left-0 w-[250px] sm:w-[400px] h-[250px] sm:h-[400px] bg-[#0D3F7F]/25 rounded-full blur-[80px] sm:blur-[120px] pointer-events-none" />

      {/* Subtle Grid */}
      <div className="absolute inset-0 opacity-[0.025]" style={{ backgroundImage: `linear-gradient(#ffffff 1px, transparent 1px), linear-gradient(90deg, #ffffff 1px, transparent 1px)`, backgroundSize: '28px 28px' }} />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        
        {/* ── HEADER ── */}
        <div className="text-center max-w-2xl mx-auto mb-8 sm:mb-10">
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="inline-flex items-center gap-2 bg-[#0D3F7F]/40 border border-[#1E5CB3]/40 px-3.5 py-1 rounded-full mb-3 backdrop-blur-md"
          >
            <ShieldCheck className="w-3.5 h-3.5 text-[#F7941D]" />
            <span className="text-[9px] sm:text-[10px] font-bold tracking-[0.15em] text-[#F0F7FF] uppercase">
              Govt. Initiative 2024
            </span>
          </motion.div>

          <motion.h2
            initial={{ opacity: 0, y: 15 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="text-2xl sm:text-3xl md:text-4xl font-black text-white tracking-tight leading-tight"
          >
            Unlock Massive{' '}
            <span className="bg-gradient-to-r from-[#F7941D] to-[#FFC107] bg-clip-text text-transparent">
              Government Subsidies
            </span>
          </motion.h2>
        </div>

        {/* ── BENTO GRID ── */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-5 sm:gap-6">
          
          {/* LEFT: Image Highlight Card */}
          <motion.div
            initial={{ opacity: 0, x: -15 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-30px" }}
            className="lg:col-span-5 relative rounded-2xl overflow-hidden group border border-[#1E5CB3]/30 h-[260px] sm:h-[320px] lg:h-auto lg:min-h-0 flex flex-col justify-end p-5 sm:p-6"
          >
            {/* BG Image */}
            <div className="absolute inset-0 z-0">
              <img 
                src={image} 
                alt="Solar Panels Subsidy" 
                className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" 
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#050B14] via-[#050B14]/85 to-transparent" />
            </div>

            {/* Content */}
            <div className="relative z-10 text-left">
              <div className="bg-[#F7941D] text-white text-[9px] font-black uppercase tracking-widest px-2.5 py-0.5 rounded-md inline-block mb-2 shadow-[0_0_12px_rgba(247,148,29,0.4)]">
                PM Surya Ghar Yojna
              </div>
              <h3 className="text-2xl sm:text-3xl font-black text-white tracking-tight leading-none mb-1.5">
                Up to <span className="text-[#FFC107]">₹78,000</span>
              </h3>
              <p className="text-slate-300 text-xs sm:text-[13px] font-medium leading-relaxed max-w-xs">
                Direct bank subsidy. We handle 100% paperwork.
              </p>
            </div>
          </motion.div>

          {/* RIGHT: Details Column */}
          <div className="lg:col-span-7 flex flex-col gap-5">
            
            {/* Financial Benefits Grid */}
            <motion.div
              initial={{ opacity: 0, x: 15 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: "-30px" }}
              className="bg-white/5 backdrop-blur-xl border border-white/10 p-4 sm:p-5 rounded-2xl"
            >
              <h4 className="text-[11px] sm:text-[12px] text-white font-bold uppercase tracking-widest mb-3.5 flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-[#FFC107]" /> Financial Benefits
              </h4>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                {financialBenefits.map((item, idx) => (
                  <div 
                    key={idx} 
                    className="bg-white/5 border border-white/10 hover:border-[#F7941D]/40 p-3.5 rounded-xl transition-all duration-300 hover:-translate-y-0.5 hover:bg-white/10 group"
                  >
                    <div className="flex items-start gap-3">
                      <div className="w-9 h-9 rounded-full bg-[#0D3F7F]/60 flex items-center justify-center text-[#FFC107] shrink-0 group-hover:scale-110 transition-transform">
                        <item.icon className="w-4 h-4" />
                      </div>
                      <div>
                        <h5 className="text-white text-[13px] font-bold leading-tight mb-1">{item.title}</h5>
                        <p className="text-slate-400 text-[11px] leading-snug">{item.desc}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Eligibility Strip */}
            <motion.div
              initial={{ opacity: 0, y: 15 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-30px" }}
              transition={{ delay: 0.15 }}
              className="bg-gradient-to-r from-[#0D3F7F]/30 to-[#1E5CB3]/10 border border-[#1E5CB3]/30 p-4 sm:p-5 rounded-2xl"
            >
              <h4 className="text-[11px] sm:text-[12px] text-white font-bold uppercase tracking-widest mb-3.5 flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-[#F7941D]" /> Basic Requirements
              </h4>
              <div className="grid grid-cols-1 xs:grid-cols-2 sm:grid-cols-4 gap-y-3.5 gap-x-4">
                {eligibility.map((req, idx) => (
                  <div key={idx} className="flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 text-[#F7941D] shrink-0 mt-0.5" />
                    <span className="text-[11px] sm:text-[12px] text-slate-300 font-medium leading-tight">{req}</span>
                  </div>
                ))}
              </div>
            </motion.div>

          </div>
        </div>

      </div>
    </section>
  );
};

export default SubsidySection;